import { ENVIRONMENT_CATEGORY } from '@dealstack/shared/lib/common/environment';
import { escapeEmailHtml } from '@dealstack/shared/lib/common/liquidJs/emailLiquidJs';
import { isSsoFixtureDomainEmail } from '@dealstack/shared/lib/common/ssoFixture';

const TEST_MAIL_NAMESPACE = 'rf3z6';
const TEST_MAIL_DOMAIN = 'inbox.testmail.app';

export const normaliseEmail = (email: string): string => email.trim().toLowerCase();

export const extractDomain = (email: string): string | null => {
	const domainRegex = /@([a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/;
	const match = email.match(domainRegex);

	return match?.[1] ?? null;
};

export const getLocalSafeEmail = (email: string, env: string): string => {
	const isLocalEnv = env === ENVIRONMENT_CATEGORY.LOCAL;

	if (!isLocalEnv) {
		return email;
	}

	if (
		email.endsWith('@dealstack.io') ||
		email.endsWith('@allfront.io') ||
		email.endsWith('@inbox.testmail.app')
	) {
		return email;
	}
	const localPart = email.split('@')[0];

	return `${TEST_MAIL_NAMESPACE}.${localPart}@${TEST_MAIL_DOMAIN}`;
};

export interface TestMailbox {
	namespace: string;
	domain: string;
}

/** Mirrors the TESTMAIL_NAMESPACE / TESTMAIL_DOMAIN defaults in libs/nest-app/src/config/env.ts. */
export const DEFAULT_TEST_MAILBOX: TestMailbox = {
	namespace: TEST_MAIL_NAMESPACE,
	domain: TEST_MAIL_DOMAIN,
};

/**
 * `alice+x@sso-a.dealstack-dev.com` -> `rf3z6.sso-a.alice+x@inbox.testmail.app`. The subdomain label
 * stays in the tag so the three fixture domains never collapse into one mailbox.
 */
export const toTestMailboxAddress = (
	email: string,
	mailbox: TestMailbox = DEFAULT_TEST_MAILBOX
): string => {
	const localPart = email.split('@')[0] ?? email;
	const label = extractDomain(email)?.split('.')[0]?.toLowerCase();
	const tag = label ? `${label}.${localPart}` : localPart;

	return `${mailbox.namespace}.${tag}@${mailbox.domain}`;
};

/**
 * The address a provider should actually send to. Fixture domains have no MX record, so their mail is
 * rerouted from every non-production environment - not just local, which is all `getLocalSafeEmail`
 * covers. Mail to real reviewers on a named environment is deliberately left alone.
 */
export const getDeliverableRecipient = (
	email: string,
	env: string,
	mailbox: TestMailbox = DEFAULT_TEST_MAILBOX
): string => {
	if (env === ENVIRONMENT_CATEGORY.PRODUCTION) {
		return email;
	}
	if (isSsoFixtureDomainEmail(email)) {
		return toTestMailboxAddress(email, mailbox);
	}

	return getLocalSafeEmail(email, env);
};

export const getSoftDeletedEmail = (email: string): string => `__deleted-${Date.now()}__${email}`;

/**
 * Converts line breaks in the given text to HTML <br /> tags,
 * returning an object with `custom_text` if text is provided.
 *
 * Regex explanation:
 * - (\r\n|\r|\n) matches all common line break types:
 *   - \r\n: Windows (CRLF)
 *   - \r: older Mac systems (CR)
 *   - \n: Unix/Linux/macOS (LF)
 */
export const formatEmailTextToHtmlParam = (emailText?: string): { custom_text?: string } => {
	if (!emailText) {
		return {};
	}
	return {
		custom_text: escapeEmailHtml(emailText).replace(/(\r\n|\r|\n)/g, '<br />'),
	};
};

export const toEmailRenderValues = (
	values: Record<string, string | undefined>
): Record<string, string | undefined> => ({
	...values,
	...formatEmailTextToHtmlParam(values['custom_text']),
});
